import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOHead from '@/components/base/SEOHead';
import { articles, categories } from '@/mocks/articles';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen pt-20 flex items-center justify-center">
          <div className="text-center">
            <h1 className="font-heading text-2xl text-foreground-800 mb-3">文章未找到</h1>
            <Link to="/" className="text-primary-500 hover:text-primary-600 font-label text-sm cursor-pointer">返回首页</Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const category = categories.find((c) => c.slug === article.category);
  const relatedArticles = articles
    .filter((a) => a.category === article.category && a.id !== article.id)
    .slice(0, 3);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.date,
    image: article.coverImage,
    url: `https://elegantvibe.github.io/article/${slug}`,
    publisher: {
      '@type': 'Organization',
      name: '熟女魅力',
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://elegantvibe.github.io/' },
      { '@type': 'ListItem', position: 2, name: category?.name || '分类', item: `https://elegantvibe.github.io/category/${article.category}` },
      { '@type': 'ListItem', position: 3, name: article.title },
    ],
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: `什么是${article.tags[0] || '熟女魅力'}？`,
        acceptedAnswer: {
          '@type': 'Answer',
          text: article.excerpt,
        },
      },
      {
        '@type': 'Question',
        name: `成熟女性如何展现${article.category === 'shishang-chuanda' ? '时尚品味' : article.category === 'hufu-baoyang' ? '健康肌肤' : '独特魅力'}？`,
        acceptedAnswer: {
          '@type': 'Answer',
          text: article.excerpt,
        },
      },
    ],
  };

  return (
    <>
      <SEOHead
        title={`${article.title} | 熟女魅力`}
        description={article.excerpt}
        keywords={article.tags.join(',')}
        canonicalUrl={`https://elegantvibe.github.io/article/${slug}`}
        ogType="article"
        articleData={{
          publishedTime: article.date,
          author: article.author,
          tags: article.tags,
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema, null, 2) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema, null, 2) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema, null, 2) }}
      />
      <Navbar />
      <main className="min-h-screen pt-20">
        <article className="max-w-4xl mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', path: '/' },
              { label: category?.name || '分类', path: `/category/${article.category}` },
              { label: article.title },
            ]}
          />

          <div className="relative w-full h-64 md:h-96 rounded-2xl overflow-hidden mb-8">
            <img
              src={article.coverImage}
              alt={article.title}
              className="w-full h-full object-cover object-top"
            />
          </div>

          <div className="flex flex-wrap items-center gap-3 mb-4">
            {article.tags.map((tag) => (
              <span key={tag} className="px-3 py-1 bg-primary-50 text-primary-600 text-xs font-label rounded-full">
                {tag}
              </span>
            ))}
          </div>

          <h1 className="font-heading text-2xl md:text-4xl font-semibold text-foreground-900 mb-4 leading-tight" style={{ fontFamily: 'var(--font-heading)' }}>
            {article.title}
          </h1>

          <div className="flex items-center gap-4 text-sm text-foreground-500 font-label mb-8 pb-8 border-b border-background-200">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
                <i className="ri-user-line text-sm"></i>
              </div>
              <span className="font-medium text-foreground-700">{article.author}</span>
            </div>
            <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
            <span>{article.date}</span>
            <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
            <span>阅读 {article.readTime}</span>
          </div>

          <div
            className="prose prose-lg max-w-none mb-12 font-body text-foreground-700 leading-relaxed"
            style={{ fontFamily: 'var(--font-body)' }}
            dangerouslySetInnerHTML={{
              __html: article.content
                .replace(/<h2>/g, '<h2 class="font-heading text-xl md:text-2xl font-semibold text-foreground-900 mt-10 mb-4" style="font-family: var(--font-heading)">')
                .replace(/<p>/g, '<p class="mb-4 text-foreground-700 leading-loose">')
                .replace(/<strong>/g, '<strong class="text-foreground-900 font-semibold">'),
            }}
          />
        </article>

        {relatedArticles.length > 0 && (
          <section className="max-w-4xl mx-auto px-4 md:px-6 pb-16">
            <h2 className="font-heading text-xl font-semibold text-foreground-900 mb-6" style={{ fontFamily: 'var(--font-heading)' }}>
              相关推荐
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {relatedArticles.map((ra) => (
                <Link
                  key={ra.id}
                  to={`/article/${ra.slug}`}
                  className="group block bg-background-50 rounded-xl overflow-hidden border border-background-200/70 hover:border-primary-200 transition-all duration-300 cursor-pointer"
                >
                  <div className="relative w-full h-40 overflow-hidden">
                    <img
                      src={ra.coverImage}
                      alt={ra.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-4">
                    <h3 className="font-heading text-sm font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2" style={{ fontFamily: 'var(--font-heading)' }}>
                      {ra.title}
                    </h3>
                    <p className="text-xs text-foreground-400 font-label mt-2">{ra.date}</p>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}
      </main>
      <Footer />
    </>
  );
}